/**
 * SplashScreen - Miraero Games Official Splash & Loading Screen
 * Displays the user's painted Terra Anima splash art, dynamic localized subtitle
 * ("솔라리스 왕국으로 가는 길" / "Path to the Solaris Kingdom"),
 * the progress bar, and a blinking prompt ("아무 키나 눌러 시작" / "Press any key to start").
 */

export interface SplashI18n {
  subtitle: string;
  loading: string;
  pressAnyKey: string;
  pressTouch: string;
}

const SPLASH_LOCALES: Record<string, SplashI18n> = {
  ko: {
    subtitle: '솔라리스 왕국으로 가는 길',
    loading: 'Loading...',
    pressAnyKey: '아무 키나 눌러 시작',
    pressTouch: '화면을 터치하여 시작',
  },
  en: {
    subtitle: 'Path to the Solaris Kingdom',
    loading: 'Loading...',
    pressAnyKey: 'Press any key to start',
    pressTouch: 'Tap screen to start',
  },
  ja: {
    subtitle: 'ソラリス王国への道',
    loading: 'Loading...',
    pressAnyKey: '任意のキーを押して開始',
    pressTouch: '画面をタップして開始',
  },
};

export class SplashScreen {
  private el: HTMLElement | null = null;
  private barFill: HTMLElement | null = null;
  private titleTextEl: HTMLElement | null = null;
  private statusTextEl: HTMLElement | null = null;

  private live = false;
  private ready = false;
  private wanted = false;
  private shownProgress = 0;
  private externalReady = false;
  private startTime = 0;
  private lang = 'ko';

  private onCompleteCallback: (() => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  public init(onComplete?: () => void): void {
    this.onCompleteCallback = onComplete || null;
    const el = document.getElementById('splash');
    if (!el) {
      if (this.onCompleteCallback) this.onCompleteCallback();
      return;
    }

    this.el = el;
    this.live = true;
    this.ready = false;
    this.wanted = false;
    this.shownProgress = 0;
    this.startTime = performance.now();

    // Language detection (defaults to ko, supports localStorage aln.lang / navigator.language)
    try {
      const savedLang = localStorage.getItem('aln.lang');
      if (savedLang && SPLASH_LOCALES[savedLang]) {
        this.lang = savedLang;
      } else {
        const navLang = (navigator.language || 'ko').slice(0, 2).toLowerCase();
        if (SPLASH_LOCALES[navLang]) {
          this.lang = navLang;
        }
      }
    } catch {
      this.lang = 'ko';
    }

    this.titleTextEl = el.querySelector('#splash-title-text');
    this.statusTextEl = el.querySelector('#splash-status-text');
    this.barFill = el.querySelector('.splash-bar i');

    this.applyTexts();

    this.keyHandler = (e: KeyboardEvent) => {
      if (!this.live) return;
      if (e.ctrlKey || e.metaKey || e.altKey || /^F\d+$/.test(e.key)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.tap();
    };

    window.addEventListener('keydown', this.keyHandler, true);
    el.addEventListener('click', () => this.tap());
    el.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', (e) => e.stopPropagation());
    });

    this.frame = this.frame.bind(this);
    requestAnimationFrame(this.frame);
  }

  public setLanguage(lang: string): void {
    if (SPLASH_LOCALES[lang]) {
      this.lang = lang;
      this.applyTexts();
    }
  }

  private applyTexts(): void {
    const locale = SPLASH_LOCALES[this.lang] || SPLASH_LOCALES.ko;
    if (this.titleTextEl) {
      this.titleTextEl.textContent = locale.subtitle;
    }
    if (this.statusTextEl) {
      if (this.ready) {
        const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        this.statusTextEl.textContent = isTouch ? locale.pressTouch : locale.pressAnyKey;
      } else {
        // While loading, the picture already displays 'Loading...', so keep empty or subtle dots
        this.statusTextEl.textContent = '';
      }
    }
  }

  public notifyEngineLoaded(): void {
    this.externalReady = true;
  }

  private tap(): void {
    if (!this.el) return;
    if (this.ready) {
      this.close();
    } else {
      this.wanted = true;
    }
  }

  private close(): void {
    if (!this.el) return;
    const el = this.el;
    this.el = null;
    if (this.keyHandler) {
      window.removeEventListener('keydown', this.keyHandler, true);
      this.keyHandler = null;
    }
    el.classList.add('out');
    setTimeout(() => {
      this.live = false;
      el.remove();
      if (this.onCompleteCallback) {
        this.onCompleteCallback();
      }
    }, 420);
  }

  private computeProgress(now: number): { done: boolean; value: number } {
    const elapsed = (now - this.startTime) / 1000;
    const timeRatio = Math.min(1, elapsed / 1.0);
    const done = timeRatio >= 1 && this.externalReady;
    return { done, value: timeRatio };
  }

  private frame(now: number): void {
    if (!this.live) return;

    if (this.el && !this.ready) {
      const p = this.computeProgress(now);
      this.shownProgress = p.done ? 1 : Math.max(this.shownProgress, Math.min(0.95, p.value));
      if (this.barFill) {
        this.barFill.style.transform = `scaleX(${this.shownProgress})`;
      }
      if (p.done) {
        this.ready = true;
        this.el.classList.add('ready');
        this.applyTexts();
        if (this.wanted) {
          this.close();
        }
      }
    }

    requestAnimationFrame(this.frame);
  }
}
